import { Component, Input } from '@angular/core';
import type { FormArray, FormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import type { Observable } from 'rxjs';

import { UserSelectContainerComponent } from '@recruitee/account-ui';
import { CurrencySelectFieldComponent } from '@recruitee/currency-ui';
import { CustomFieldFormGroup } from '@recruitee/custom-fields-types';
import { CustomFieldComponent, CustomFieldViewContext } from '@recruitee/custom-fields-ui';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { SelectOption } from '@recruitee/user-interface';
import {
  BadgeComponent,
  BoxComponent,
  FixedSelectOptionsHelper,
  FormFieldComponent,
  HeadingComponent,
  IconComponent,
  IconHelpComponent,
  LabelComponent,
  NumericInputComponent,
  RtTooltip,
  SelectComponent,
} from '@recruitee/user-interface';

import type { RequisitionForm, AvailableFieldsMap, FormBox } from '../../../requisitions.types';
import { RequisitionFormManagerService } from '../../../services/requisition-form-manager.service';
import { DynamicSelectFieldComponent } from '../../form-elements/dynamic-select-field/dynamic-select-field.component';
import { SalaryFormGroupComponent } from '../../form-elements/salary-form-group/salary-form-group.component';

type FixedSelectOptions = {
  categories: SelectOption[];
  educations: SelectOption[];
  employmentTypes: SelectOption[];
  experiences: SelectOption[];
  salaryPeriods: SelectOption[];
  priorities: SelectOption[];
};

@Component({
  selector: 'rt-requisitions-details-form',
  templateUrl: './requisitions-details-form.component.html',
  styleUrls: ['./requisitions-details-form.component.less'],
  imports: [
    BadgeComponent,
    BoxComponent,
    FormFieldComponent,
    HeadingComponent,
    IconHelpComponent,
    LabelComponent,
    RtTooltip,
    SalaryFormGroupComponent,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    NumericInputComponent,
    UserSelectContainerComponent,
    CustomFieldComponent,
    CurrencySelectFieldComponent,
    DynamicSelectFieldComponent,
    IconComponent,
  ],
})
export class RequisitionsDetailsFormComponent {
  protected readonly CustomFieldViewContext = CustomFieldViewContext;

  @Input() public form: FormGroup<RequisitionForm>;
  @Input() public availableDetailsFields: AvailableFieldsMap[FormBox.Details];
  @Input() public departmentSelectOptions: SelectOption[];
  @Input() public isFormEditable: boolean;
  @Input() public isDepartmentsMode: boolean;

  public selectOptions: FixedSelectOptions = {
    employmentTypes: this.fixedSelectOptions.getEmploymentTypeSelectOptions(),
    educations: this.fixedSelectOptions.getEducationSelectOptions(),
    experiences: this.fixedSelectOptions.getExperienceSelectOptions(),
    categories: this.fixedSelectOptions.getCategorySelectOptions(),
    salaryPeriods: this.fixedSelectOptions.getSalaryPeriodSelectOptions(),
    priorities: this.fixedSelectOptions.getPrioritySelectOptions(),
  };

  public formFieldsConfig = {
    department: {
      label: this.i18n.translate('app.offer_form.department'),
      searchPlaceholder: this.i18n.translate('app.common.placeholders.search.departments.other'),
      formControlName: 'departmentId',
    },
  };

  constructor(
    private fixedSelectOptions: FixedSelectOptionsHelper,
    private i18n: I18nService,
    public requisitionFormManager: RequisitionFormManagerService,
  ) {}

  public get requisitionUpdated$(): Observable<void> {
    return this.requisitionFormManager.updated$;
  }

  public setUserSelectFieldValue(fieldName: string, value: number | null) {
    this.form.get(fieldName)?.setValue(value);
    this.form.markAsDirty();
  }

  public hasFormGroupAllFieldsFilled(formGroup?: FormGroup): boolean {
    if (!formGroup) return false;
    return Object.values(formGroup.controls).every(control => control.value);
  }

  public get customFieldsFormArray(): FormArray<CustomFieldFormGroup> | undefined {
    return this.form.controls.customFields;
  }
}
